import React, { useEffect } from 'react';
import './App.css';
import AnalyticsRouteTracker from './components/AnalyticsRouteTracker';
import { setAnalyticsUserType } from './analytics';
import hoggedinIcon from './hoggedin-icon-white.png';
import PeopleList from './PeopleList';
import HoggedInPage from './HoggedInPage';
import HoggedInPublicPage from './HoggedInPublicPage';
import DashboardPage from './DashboardPage';
import { useAppAuth } from './auth/AppAuthProvider';
import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom';
import { Box, Flex, Button, Container, Text, HStack, Link as ChakraLink, Image, Avatar, Menu, MenuButton, MenuList, MenuItem, VisuallyHidden, StackDivider, Alert, AlertIcon, AlertTitle, AlertDescription } from '@chakra-ui/react';

const AuthenticatedShell = () => {
  const { isLoading, error, isAuthenticated, user, isAdmin, selfFetchError, signIn, signOut } = useAppAuth();

  useEffect(() => {
    if (isAuthenticated) {
      setAnalyticsUserType(isAdmin ? 'admin' : 'user');
    }
  }, [isAuthenticated, isAdmin]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Encountering error... {error.message}</div>;
  }

  if (!isAuthenticated) {
    return (
      <Container className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
        <h2 className="mb-4">You are not signed in.</h2>
        <Button variant="primary" size="lg" onClick={signIn}>
          Sign In
        </Button>
      </Container>
    );
  }

  const userName = user?.profile?.name || user?.profile?.given_name || user?.profile?.email;
  const navLinks = isAdmin
    ? [
        { label: 'Home', to: '/' },
        { label: 'People', to: '/people' }
      ]
    : [{ label: 'Home', to: '/' }];

  return (
    <Flex direction="column" minH="100vh" bg="gray.50">
      <Box as="header" bg="blue.500" color="white" py={4}>
        <Container maxW="container.xl">
          <Flex justify="space-between" align="center">
            <HStack spacing={4} align="center">
              <ChakraLink as={Link} to="/">
                <Image
                  src={hoggedinIcon}
                  alt="HoggedIn"
                  maxW="60px"
                  height="auto"
                  objectFit="contain"
                />
              </ChakraLink>
              <HStack spacing={4} divider={<StackDivider borderColor="whiteAlpha.600" borderWidth="1px" />}>
                {navLinks.map(item => (
                  <ChakraLink key={item.to} as={Link} to={item.to}>
                    {item.label}
                  </ChakraLink>
                ))}
              </HStack>
              {!isAdmin && (
                <Button
                  as={Link}
                  to="/hoggedin"
                  size="sm"
                  variant="outline"
                  color="white"
                  borderColor="whiteAlpha.700"
                  _hover={{ bg: 'whiteAlpha.200' }}
                >
                  Profile
                </Button>
              )}
            </HStack>
            <Menu placement="bottom-end" gutter={8} isLazy>
              <MenuButton
                as={Button}
                variant="ghost"
                px={2}
                py={1}
                _hover={{ bg: 'whiteAlpha.200' }}
                _active={{ bg: 'whiteAlpha.300' }}
                color="white"
              >
                <HStack spacing={0} align="center">
                  <VisuallyHidden>{userName}</VisuallyHidden>
                  <Avatar
                    name={userName}
                    size="md"
                    bg="white"
                    color="blue.600"
                    fontWeight="bold"
                  />
                </HStack>
              </MenuButton>
              <MenuList
                bg="blue.500"
                borderColor="blue.400"
                color="white"
                py={0}
                minW="140px"
              >
                <MenuItem
                  bg="blue.600"
                  color="white"
                  fontWeight="medium"
                  fontSize="md"
                  _hover={{ bg: 'blue.500' }}
                  _focus={{ bg: 'blue.500' }}
                  onClick={signOut}
                >
                  Sign Out
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Container>
      </Box>

      {selfFetchError && (
        <Box bg="yellow.50" borderBottom="1px solid" borderColor="yellow.100">
          <Container maxW="container.xl" py={4}>
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertTitle mr={2}>Profile unavailable</AlertTitle>
              <AlertDescription>{selfFetchError}</AlertDescription>
            </Alert>
          </Container>
        </Box>
      )}

      <Container as="main" maxW="container.xl" flex="1" py={6}>
        <Routes>
          <Route path="hoggedin" element={<HoggedInPage />} />
          <Route path="/" element={<DashboardPage />} />
          <Route path="people" element={<PeopleList />} />
        </Routes>
      </Container>

      <Box as="footer" bg="blue.600" color="white" py={3} textAlign="center">
        <Text fontSize="sm">&copy; 2026 HoggedIn</Text>
      </Box>
    </Flex>
  );
};

function App() {
  return (
    <Router>
      <AnalyticsRouteTracker />
      <Routes>
        <Route path="/share/:token" element={<HoggedInPublicPage />} />
        <Route path="/*" element={<AuthenticatedShell />} />
      </Routes>
    </Router>
  );
}

export default App;
